import { addMonths, isValid } from 'date-fns';
/** Le paiement en 3/4x d'un dossier existant n'est ouvert que jusqu'à 3 mois avant le départ. */
export const INSTALLMENT_PAYMENT_MIN_MONTHS_BEFORE_DEPARTURE = 3;
/** Plancher du prestataire de paiement fractionné, en centimes (50 €). */
export const INSTALLMENT_PAYMENT_MIN_AMOUNT_CENTS = 5000;
/** Plafond du prestataire de paiement fractionné, en centimes (6 000 €). */
export const INSTALLMENT_PAYMENT_MAX_AMOUNT_CENTS = 600000;
/**
 * Motifs de refus du paiement en plusieurs fois sur un dossier existant.
 *
 * Source de vérité unique : le type en est DÉRIVÉ, et le serveur comme le front lisent ce
 * même tableau. Un motif ajouté ici est donc automatiquement connu des deux côtés — une
 * liste recopiée à la main ne lèverait aucune erreur de compilation en oubliant une valeur,
 * et le front retomberait silencieusement sur son message générique.
 *
 * L'ordre d'évaluation, lui, est figé dans {@link checkInstallmentPaymentEligibility} : le
 * front n'affiche que le premier motif rencontré.
 */
export const INSTALLMENT_PAYMENT_INELIGIBILITY_REASONS = [
    /** Solde restant <= 0 : il n'y a plus rien à régler. */
    'ALREADY_SETTLED',
    /** Solde restant au-dessus du plafond prestataire. */
    'AMOUNT_TOO_HIGH',
    /** Solde restant sous le plancher prestataire. */
    'AMOUNT_TOO_LOW',
    'BOOKING_CANCELLED',
    /** Départ à moins de 3 mois. */
    'DEPARTURE_TOO_CLOSE',
    /** Un règlement 3/4x est déjà encaissé sur le dossier : pas de cumul. */
    'INSTALLMENT_ALREADY_USED',
];
/**
 * Décide si le solde d'un dossier existant peut être réglé en 3 ou 4 fois.
 *
 * Fonction pure, partagée entre le serveur (qui l'impose) et le front (qui affiche le bouton
 * et le motif de refus) : les deux doivent trancher exactement pareil, sinon le client voit un
 * parcours que le serveur refusera ensuite.
 *
 * L'ordre des refus est significatif — le front ne montre que le premier motif, on va donc du
 * plus structurant (dossier annulé) au plus circonstanciel (montant hors bornes).
 *
 * Une date de départ non parsable est traitée comme `DEPARTURE_TOO_CLOSE` : on préfère ne pas
 * proposer le fractionné plutôt que de l'ouvrir sur une date qu'on ne sait pas lire.
 */
export function checkInstallmentPaymentEligibility(input) {
    if (input.isCancelled)
        return { eligible: false, reason: 'BOOKING_CANCELLED' };
    if (input.hasSettledInstallmentPayment) {
        return { eligible: false, reason: 'INSTALLMENT_ALREADY_USED' };
    }
    const remainingCents = input.totalPriceCents - input.paidCents;
    if (remainingCents <= 0)
        return { eligible: false, reason: 'ALREADY_SETTLED' };
    const departure = input.departureDate instanceof Date ? input.departureDate : new Date(input.departureDate);
    const deadline = addMonths(input.now, INSTALLMENT_PAYMENT_MIN_MONTHS_BEFORE_DEPARTURE);
    if (!isValid(departure) || departure.getTime() < deadline.getTime()) {
        return { eligible: false, reason: 'DEPARTURE_TOO_CLOSE' };
    }
    if (remainingCents < INSTALLMENT_PAYMENT_MIN_AMOUNT_CENTS) {
        return { eligible: false, reason: 'AMOUNT_TOO_LOW' };
    }
    if (remainingCents > INSTALLMENT_PAYMENT_MAX_AMOUNT_CENTS) {
        return { eligible: false, reason: 'AMOUNT_TOO_HIGH' };
    }
    return { amountCents: remainingCents, eligible: true };
}
/**
 * Libellé client d'un refus de paiement fractionné.
 *
 * Centralisé ici pour que le PDF du contrat, l'espace client et le tunnel de règlement
 * expliquent le refus avec les mêmes mots.
 */
export function getInstallmentPaymentIneligibilityMessage(reason, context) {
    switch (reason) {
        case 'ALREADY_SETTLED':
            return 'Ce dossier est déjà entièrement réglé.';
        case 'AMOUNT_TOO_HIGH':
            // Seul refus réversible (cf. isInstallmentPaymentIneligibilityFinal), et le client
            // peut y arriver depuis le bouton d'un contrat : on lui dit quoi faire, sinon le
            // message se lit comme une porte fermée alors qu'il tient la clé.
            return `Le paiement en 3 ou 4 fois est limité à ${formatEuros(INSTALLMENT_PAYMENT_MAX_AMOUNT_CENTS)}, et le solde de votre dossier dépasse ce plafond. Réglez d'abord une partie par carte bancaire : dès que le solde restant passe sous ${formatEuros(INSTALLMENT_PAYMENT_MAX_AMOUNT_CENTS)}, ce mode de règlement redevient disponible.`;
        case 'AMOUNT_TOO_LOW':
            return `Le paiement en 3 ou 4 fois n'est possible qu'à partir de ${formatEuros(INSTALLMENT_PAYMENT_MIN_AMOUNT_CENTS)}. Le solde de votre dossier est inférieur à ce montant.`;
        case 'BOOKING_CANCELLED':
            return 'Ce dossier a été annulé.';
        case 'DEPARTURE_TOO_CLOSE':
            return context?.departureDateLabel
                ? `Le paiement en 3 ou 4 fois n'est possible que jusqu'à ${INSTALLMENT_PAYMENT_MIN_MONTHS_BEFORE_DEPARTURE} mois avant le départ. Votre départ étant prévu le ${context.departureDateLabel}, ce mode de règlement n'est plus disponible.`
                : `Le paiement en 3 ou 4 fois n'est possible que jusqu'à ${INSTALLMENT_PAYMENT_MIN_MONTHS_BEFORE_DEPARTURE} mois avant le départ, or votre départ est trop proche.`;
        case 'INSTALLMENT_ALREADY_USED':
            return 'Un paiement en plusieurs fois est déjà en place sur ce dossier.';
    }
}
/**
 * Ce refus est-il DÉFINITIF pour ce dossier, ou peut-il se lever de lui-même ?
 *
 * Distinction indispensable aux supports FIGÉS — le PDF du contrat est généré une fois,
 * archivé et envoyé par mail, puis consulté pendant des mois. Il ne doit pas décider
 * « éligible aujourd'hui ? » mais « ce dossier pourra-t-il l'être un jour ? ».
 *
 * Un seul motif est réversible, et c'est une conséquence directe du sens de variation du
 * solde : il ne fait que DÉCROÎTRE à mesure des acomptes.
 * - `AMOUNT_TOO_HIGH` se lève donc tout seul dès que le solde repasse sous le plafond ;
 * - `AMOUNT_TOO_LOW` est définitif pour exactement la même raison ;
 * - `DEPARTURE_TOO_CLOSE` est définitif car le temps ne recule pas ;
 * - annulation et cumul ne se défont pas.
 *
 * `ALREADY_SETTLED` est traité comme définitif : un réajustement tarifaire peut en théorie
 * refaire apparaître un solde, mais c'est trop marginal pour imprimer un bouton dessus.
 *
 * Les surfaces VIVANTES (espace client, /payer-acompte) n'utilisent pas cette fonction :
 * elles réévaluent l'éligibilité réelle à chaque affichage.
 */
export function isInstallmentPaymentIneligibilityFinal(reason) {
    return reason !== 'AMOUNT_TOO_HIGH';
}
/** Garde de type sur une valeur venue du réseau (corps d'erreur HTTP, query string…). */
export function isInstallmentPaymentIneligibilityReason(value) {
    return (typeof value === 'string' &&
        INSTALLMENT_PAYMENT_INELIGIBILITY_REASONS.includes(value));
}
const eurosFormatter = new Intl.NumberFormat('fr-FR', {
    currency: 'EUR',
    maximumFractionDigits: 0,
    style: 'currency',
});
/** Formate un montant en centimes pour un message client, ex: 600_000 -> "6 000 €". */
function formatEuros(cents) {
    return eurosFormatter.format(cents / 100);
}
